import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt } from "@fortawesome/free-solid-svg-icons";
import {
  DropdownFilter,
  FloatInput,
  FloatNumericInput,
  FloatSelect,
  showToast,
  WizardFooter,
} from "src/componentes";
import { ConsultarPessoa } from "src/hook/Consulta/ConsultaPessoa.Module";
import { PessoaDto } from "src/pages/Privada/Entidade/Pessoa/Pessoa.Dto";
import PessoaCadastroView from "src/pages/Privada/Entidade/Pessoa/Cadastro/PessoaCadastro.View";
import { FormatarCNPJ, FormatarCPF } from "src/hook/Functions";
import {
  ETipoEmitenteMDFe,
  ETipoValePedagioMDFe,
  EModalMDFe,
  getLocal,
  MDFeInput,
  STORAGEMDFE_KEY,
  tipoValePedagioMDFeOptions,
  categoriaCombinacaoVeicularOptions,
  updateLocal,
} from "../MDFe.Dto";
import {
  ContratantesFormType,
  ContratantesSchema,
} from "./MDFe.Cadastro.Dto";
import { MDFeCIOTOutput } from "src/services/Fiscal.Service";
import MDFeModalGerarCIOTView from "./Modal.GerarCIOT/MDFe.Modal.GerarCIOT.View";
import {
  CiotValoresIniciais,
  montarValoresIniciaisCiot,
} from "./Modal.GerarCIOT/MDFe.Modal.GerarCIOT.Dto";
import MDFeConfiguracaoCode from "../MDFe.Configuracao.Code";
import { TipoConfiguracaoEnum } from "src/pages/Privada/Configuracoes/Sistema/Sistema.Dto";

function MDFeCadastroContratantesView() {
  const navigate = useNavigate();
  const configuracao = MDFeConfiguracaoCode();
  const pessoaBuscar = ConsultarPessoa();
  const [ciotModal, setCiotModal] = useState<{
    index: number;
    inicial: CiotValoresIniciais;
  } | null>(null);
  const [cadastroContratanteIndex, setCadastroContratanteIndex] = useState<
    number | null
  >(null);
  const [cadastroValePedagioIndex, setCadastroValePedagioIndex] = useState<
    number | null
  >(null);
  const [
    cadastroResponsavelValePedagioIndex,
    setCadastroResponsavelValePedagioIndex,
  ] = useState<number | null>(null);
  const [cadastroCiotIndex, setCadastroCiotIndex] = useState<number | null>(
    null
  );

  const mdfe = (getLocal<MDFeInput>(STORAGEMDFE_KEY) ??
    ({
      contratantes: [],
      ciots: [],
      valesPedagio: [],
      tipoEmitente: ETipoEmitenteMDFe.PrestadorServicoTransporte,
      modal: EModalMDFe.Rodoviario,
    } as unknown as MDFeInput)) as MDFeInput;

  const isRodoviario = Number(mdfe?.modal ?? 1) === 1;
  const tipoEmit = Number(mdfe?.tipoEmitente ?? 1);
  const tipoTransp = mdfe?.tipoTransportador;
  const exigeTomador =
    isRodoviario &&
    (tipoEmit === 1 ||
      tipoEmit === 3 ||
      (tipoEmit === 2 && Boolean(tipoTransp)));

  const formatarDoc = (val?: string) => {
    if (!val) return "";
    const limpo = val.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const temLetras = /[A-Z]/.test(limpo);
    if (!temLetras && limpo.length <= 11) return FormatarCPF(limpo);
    return FormatarCNPJ(limpo);
  };

  const defaultValues: ContratantesFormType = {
    contratantes: [],
    ciots: [],
    valesPedagio: [],
  };

  if (mdfe.contratantes && mdfe.contratantes.length > 0) {
    defaultValues.contratantes = mdfe.contratantes.map((c) => ({
      nomeContratante: c.nomeContratante || "",
      documento: formatarDoc(c.documento || c.cnpj || c.cpf || ""),
      idEstrangeiro: c.idEstrangeiro || "",
    }));
  }

  const ciotsOriginais = mdfe.ciots || (mdfe as any).cioTs;
  if (ciotsOriginais && ciotsOriginais.length > 0) {
    defaultValues.ciots = ciotsOriginais.map((c: any) => ({
      nomeResponsavel: c.nomeResponsavel || "",
      ciot: c.ciot || "",
      documento: formatarDoc(c.documento || c.cnpj || c.cpf || ""),
    }));
  }

  if (mdfe.valesPedagio && mdfe.valesPedagio.length > 0) {
    defaultValues.valesPedagio = mdfe.valesPedagio.map((v) => ({
      nomeFornecedoraValePedagio: v.nomeFornecedoraValePedagio || "",
      cnpjEmitenteValePedagio: v.cnpjEmitenteValePedagio
        ? FormatarCNPJ(v.cnpjEmitenteValePedagio)
        : "",
      documentoResponsavelPagamento: formatarDoc(
        v.documentoResponsavelPagamento ||
          v.cnpjResponsavelPagamento ||
          v.cpfResponsavelPagamento ||
          ""
      ),
      nomeResponsavelPagamento: v.nomeResponsavelPagamento || "",
      numeroComprovante: v.numeroComprovante || "",
      valorValePedagio: v.valorValePedagio ?? undefined,
      tipoValePedagio: v.tipoValePedagio || ETipoValePedagioMDFe.TagEletronica,
      categoriaCombinacaoVeicular: v.categoriaCombinacaoVeicular || "",
    }));
  }

  const form = useForm<Required<ContratantesFormType>>({
    resolver: yupResolver(ContratantesSchema),
    defaultValues,
  });

  const contratantesFieldArray = useFieldArray({
    control: form.control,
    name: "contratantes",
  });

  const ciotsFieldArray = useFieldArray({
    control: form.control,
    name: "ciots",
  });

  const valesPedagioFieldArray = useFieldArray({
    control: form.control,
    name: "valesPedagio",
  });

  const handleVoltar = () => {
    navigate("../pagamento");
  };

  const onSubmit = (data: ContratantesFormType) => {
    const temTomador =
      Boolean(data.contratantes && data.contratantes.length > 0) ||
      Boolean(data.ciots && data.ciots.length > 0) ||
      Boolean(data.valesPedagio && data.valesPedagio.length > 0);

    if (exigeTomador && !temTomador) {
      showToast({
        message:
          "Informações dos tomadores é obrigatória para esta operação (Rejeição 578). Informe ao menos um Contratante (infContratante), CIOT (infCIOT) ou Vale-Pedágio (valePed).",
        title: "Grupo Obrigatório (Rejeição 578)",
        variant: "Warning",
      });
      return;
    }

    const contratantesTratados =
      data.contratantes && data.contratantes.length > 0
        ? data.contratantes
            .map((c: any) => {
              const docLimpo = c?.documento
                ? c.documento.replace(/[^A-Za-z0-9]/g, "").toUpperCase()
                : undefined;
              return {
                nomeContratante: c?.nomeContratante?.trim() || undefined,
                documento: docLimpo,
                cpf: docLimpo && docLimpo.length === 11 ? docLimpo : undefined,
                cnpj: docLimpo && docLimpo.length === 14 ? docLimpo : undefined,
                idEstrangeiro: c?.idEstrangeiro?.trim() || undefined,
              };
            })
            .filter(
              (c: any) =>
                c.documento || c.cpf || c.cnpj || c.idEstrangeiro || c.nomeContratante
            )
        : undefined;

    const ciotsTratados =
      data.ciots && data.ciots.length > 0
        ? data.ciots
            .map((c: any) => {
              const docLimpo = c?.documento
                ? c.documento.replace(/[^A-Za-z0-9]/g, "").toUpperCase()
                : undefined;
              return {
                nomeResponsavel: c?.nomeResponsavel || "",
                ciot: c?.ciot ? c.ciot.replace(/\D/g, "") : "",
                documento: docLimpo,
                cpf: docLimpo && docLimpo.length === 11 ? docLimpo : undefined,
                cnpj: docLimpo && docLimpo.length === 14 ? docLimpo : undefined,
              };
            })
            .filter((c: any) => c.ciot.length > 0)
        : undefined;

    const valesTratados =
      data.valesPedagio && data.valesPedagio.length > 0
        ? data.valesPedagio
            .map((v: any) => {
              const docLimpo = v?.documentoResponsavelPagamento
                ? v.documentoResponsavelPagamento
                    .replace(/[^A-Za-z0-9]/g, "")
                    .toUpperCase()
                : undefined;
              return {
                nomeFornecedoraValePedagio: v?.nomeFornecedoraValePedagio || "",
                cnpjEmitenteValePedagio: v?.cnpjEmitenteValePedagio
                  ? v.cnpjEmitenteValePedagio
                      .replace(/[^A-Za-z0-9]/g, "")
                      .toUpperCase()
                  : undefined,
                documentoResponsavelPagamento: docLimpo,
                nomeResponsavelPagamento: v?.nomeResponsavelPagamento || "",
                cnpjResponsavelPagamento:
                  docLimpo && docLimpo.length === 14 ? docLimpo : undefined,
                cpfResponsavelPagamento:
                  docLimpo && docLimpo.length === 11 ? docLimpo : undefined,
                numeroComprovante: String(v?.numeroComprovante || "").trim(),
                valorValePedagio: Number(v?.valorValePedagio) || 0,
                tipoValePedagio:
                  (v?.tipoValePedagio as ETipoValePedagioMDFe) ||
                  ETipoValePedagioMDFe.TagEletronica,
                categoriaCombinacaoVeicular:
                  v?.categoriaCombinacaoVeicular || "",
              };
            })
            .filter(
              (v: any) => v.numeroComprovante.length > 0 && v.valorValePedagio > 0
            )
        : undefined;

    updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
      contratantes:
        contratantesTratados && contratantesTratados.length > 0
          ? contratantesTratados
          : undefined,
      ciots: ciotsTratados && ciotsTratados.length > 0 ? ciotsTratados : undefined,
      valesPedagio:
        valesTratados && valesTratados.length > 0 ? valesTratados : undefined,
    });

    navigate("../finalizacao");
  };

  const onError = (errors: any) => {
    if (errors.ciots) {
      let msg = "";
      if (Array.isArray(errors.ciots)) {
        for (const c of errors.ciots) {
          if (c?.message) { msg = c.message; break; }
          if (c?.ciot?.message) { msg = c.ciot.message; break; }
          if (c?.documento?.message) { msg = c.documento.message; break; }
          if (c?.cpf?.message) { msg = c.cpf.message; break; }
          if (c?.cnpj?.message) { msg = c.cnpj.message; break; }
        }
      }
      showToast({
        message:
          msg ||
          "O grupo 'CIOT (ANTT)' possui campos obrigatórios não preenchidos ou formato inválido.",
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }
    if (errors.valesPedagio) {
      let msg = "";
      if (Array.isArray(errors.valesPedagio)) {
        for (const v of errors.valesPedagio) {
          if (v?.message) { msg = v.message; break; }
          if (v?.cnpjEmitenteValePedagio?.message) { msg = v.cnpjEmitenteValePedagio.message; break; }
          if (v?.documentoResponsavelPagamento?.message) { msg = v.documentoResponsavelPagamento.message; break; }
          if (v?.numeroComprovante?.message) { msg = v.numeroComprovante.message; break; }
          if (v?.valorValePedagio?.message) { msg = v.valorValePedagio.message; break; }
          if (v?.categoriaCombinacaoVeicular?.message) { msg = v.categoriaCombinacaoVeicular.message; break; }
        }
      }
      showToast({
        message:
          msg ||
          "O grupo 'Vales-Pedágio' possui campos obrigatórios não preenchidos ou inconsistentes.",
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }
    if (errors.contratantes) {
      let msg = "";
      if (Array.isArray(errors.contratantes)) {
        for (const c of errors.contratantes) {
          if (c?.message) { msg = c.message; break; }
          if (c?.documento?.message) { msg = c.documento.message; break; }
          if (c?.cnpj?.message) { msg = c.cnpj.message; break; }
          if (c?.cpf?.message) { msg = c.cpf.message; break; }
        }
      } else if (errors.contratantes?.message) {
        msg = errors.contratantes.message;
      }
      showToast({
        message:
          msg ||
          "O grupo 'Contratantes do Frete' possui campos obrigatórios não preenchidos.",
        title: msg?.includes("578")
          ? "Grupo Obrigatório (Rejeição 578)"
          : "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }
    showToast({
      message: "Existem campos obrigatórios não preenchidos nesta etapa.",
      title: "Grupo Obrigatório",
      variant: "Warning",
    });
  };

  const handlePessoaSuccess = (value: PessoaDto) => {
    if (cadastroContratanteIndex !== null) {
      form.setValue(
        `contratantes.${cadastroContratanteIndex}.nomeContratante`,
        value.nome
      );
      form.setValue(
        `contratantes.${cadastroContratanteIndex}.documento`,
        formatarDoc(value.documento)
      );
    }
    setCadastroContratanteIndex(null);
  };

  const handlePessoaCancel = () => {
    setCadastroContratanteIndex(null);
  };

  const handlePessoaValePedagioSuccess = (value: PessoaDto) => {
    if (cadastroValePedagioIndex !== null) {
      form.setValue(
        `valesPedagio.${cadastroValePedagioIndex}.nomeFornecedoraValePedagio`,
        value.nome
      );
      form.setValue(
        `valesPedagio.${cadastroValePedagioIndex}.cnpjEmitenteValePedagio`,
        formatarDoc(value.documento),
        { shouldValidate: true }
      );
    }
    setCadastroValePedagioIndex(null);
  };

  const handlePessoaValePedagioCancel = () => {
    setCadastroValePedagioIndex(null);
  };

  const handlePessoaResponsavelValePedagioSuccess = (value: PessoaDto) => {
    if (cadastroResponsavelValePedagioIndex !== null) {
      form.setValue(
        `valesPedagio.${cadastroResponsavelValePedagioIndex}.nomeResponsavelPagamento`,
        value.nome
      );
      form.setValue(
        `valesPedagio.${cadastroResponsavelValePedagioIndex}.documentoResponsavelPagamento`,
        formatarDoc(value.documento),
        { shouldValidate: true }
      );
    }
    setCadastroResponsavelValePedagioIndex(null);
  };

  const handlePessoaResponsavelValePedagioCancel = () => {
    setCadastroResponsavelValePedagioIndex(null);
  };

  const handlePessoaCiotSuccess = (value: PessoaDto) => {
    if (cadastroCiotIndex !== null) {
      form.setValue(`ciots.${cadastroCiotIndex}.nomeResponsavel`, value.nome);
      form.setValue(
        `ciots.${cadastroCiotIndex}.documento`,
        formatarDoc(value.documento),
        { shouldValidate: true }
      );
    }
    setCadastroCiotIndex(null);
  };

  const handlePessoaCiotCancel = () => {
    setCadastroCiotIndex(null);
  };

  const handleGerarCiot = (index: number) => {
    const documento = form.getValues(`ciots.${index}.documento`) || "";
    const mdfeAtual = getLocal<MDFeInput>(STORAGEMDFE_KEY);

    const inicial = montarValoresIniciaisCiot(
      mdfeAtual,
      documento,
      configuracao.getConfiguracaoValor(TipoConfiguracaoEnum.RNTRC) || undefined
    );

    if (inicial.pendencias.length > 0) {
      showToast({
        message: `Preencha antes de gerar o CIOT: ${inicial.pendencias.join("; ")}.`,
        title: "Dados obrigatórios pendentes",
        variant: "Warning",
      });
      return;
    }

    setCiotModal({ index, inicial });
  };

  const handleCiotGerado = (resultado: MDFeCIOTOutput) => {
    if (ciotModal) {
      form.setValue(`ciots.${ciotModal.index}.ciot`, resultado.ciot, {
        shouldValidate: true,
      });
    }
    setCiotModal(null);
  };

  if (cadastroContratanteIndex !== null) {
    return (
      <Row className="mt-3">
        <PessoaCadastroView
          onSuccess={handlePessoaSuccess}
          onCancel={handlePessoaCancel}
        />
      </Row>
    );
  }

  if (cadastroCiotIndex !== null) {
    return (
      <Row className="mt-3">
        <PessoaCadastroView
          onSuccess={handlePessoaCiotSuccess}
          onCancel={handlePessoaCiotCancel}
        />
      </Row>
    );
  }

  if (cadastroValePedagioIndex !== null) {
    return (
      <Row className="mt-3">
        <PessoaCadastroView
          onSuccess={handlePessoaValePedagioSuccess}
          onCancel={handlePessoaValePedagioCancel}
        />
      </Row>
    );
  }

  if (cadastroResponsavelValePedagioIndex !== null) {
    return (
      <Row className="mt-3">
        <PessoaCadastroView
          onSuccess={handlePessoaResponsavelValePedagioSuccess}
          onCancel={handlePessoaResponsavelValePedagioCancel}
        />
      </Row>
    );
  }

  return (
    <>
    <Form noValidate onSubmit={form.handleSubmit(onSubmit, onError)} className="mt-3 mb-5">
      {exigeTomador && (
        <Alert variant="warning" className="mb-4">
          <strong>Informações dos Tomadores (Rejeição 578):</strong> Para esta operação com emitente Prestador de Serviço de Transporte ou TAC no modal rodoviário, é obrigatório informar ao menos um <strong>Contratante</strong>, <strong>CIOT</strong> ou <strong>Vale-Pedágio</strong>.
        </Alert>
      )}

      {/* 1. Contratantes do Frete */}
      <div className="card shadow-sm p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="text-primary mb-0">Contratantes do Frete</h5>
            <small className="text-muted">
              {exigeTomador
                ? "Tomadores do serviço (Obrigatório informar ao menos Contratante, CIOT ou Vale-Pedágio - Rejeição 578)"
                : "Tomadores / contratantes do serviço de transporte (opcional)"}
            </small>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              contratantesFieldArray.append({
                nomeContratante: "",
                documento: "",
                idEstrangeiro: "",
              })
            }
          >
            + Adicionar Contratante
          </Button>
        </div>

        {contratantesFieldArray.fields.length === 0 ? (
          <div className="text-muted small py-2">
            Nenhum contratante específico vinculado.
          </div>
        ) : (
          contratantesFieldArray.fields.map((field, index) => (
            <div key={field.id} className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold text-secondary small">
                  Contratante #{index + 1}
                </span>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => contratantesFieldArray.remove(index)}
                >
                  Remover
                </Button>
              </div>

              <Row className="g-3 align-items-start">
                <Col xs={12} md={5}>
                  <DropdownFilter<PessoaDto>
                    fetchData={pessoaBuscar.PessoaBuscar}
                    loading={pessoaBuscar.loading}
                    labelDescription="Nome / Razão Social"
                    getDescription={(item: PessoaDto) => item.nome}
                    getId={(item: PessoaDto) => item.id}
                    registeredFields={{
                      id: form.register(
                        `contratantes.${index}.nomeContratante`
                      ),
                      description: form.register(
                        `contratantes.${index}.nomeContratante`
                      ),
                    }}
                    errorsMessageDescription={
                      form.formState.errors.contratantes?.[index]
                        ?.nomeContratante
                    }
                    onSelect={(item: PessoaDto | undefined) => {
                      if (item) {
                        form.setValue(
                          `contratantes.${index}.nomeContratante`,
                          item.nome
                        );
                        form.setValue(
                          `contratantes.${index}.documento`,
                          formatarDoc(item.documento)
                        );
                      }
                    }}
                  />
                  <div className="mt-1">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none text-muted"
                      onClick={() => setCadastroContratanteIndex(index)}
                    >
                      + Cadastrar
                    </Button>
                  </div>
                </Col>
                <Col xs={12} md={4}>
                  <FloatInput
                    label="CPF / CNPJ do Contratante"
                    inputMask="cnpj-cpf"
                    maxLength={18}
                    registerField={form.register(
                      `contratantes.${index}.documento`
                    )}
                    errorsMessage={
                      form.formState.errors.contratantes?.[index]?.documento
                    }
                  />
                </Col>
                <Col xs={12} md={3}>
                  <FloatInput
                    label="Id. Estrangeiro"
                    maxLength={20}
                    registerField={form.register(
                      `contratantes.${index}.idEstrangeiro`
                    )}
                    errorsMessage={
                      form.formState.errors.contratantes?.[index]?.idEstrangeiro
                    }
                  />
                </Col>
              </Row>
            </div>
          ))
        )}
      </div>

      {/* 2. CIOT (ANTT) */}
      <div className="card shadow-sm p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="text-primary mb-0">CIOT (ANTT)</h5>
            <small className="text-muted">
              Código Identificador da Operação de Transporte (12 dígitos numéricos)
            </small>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              ciotsFieldArray.append({
                nomeResponsavel: "",
                ciot: "",
                documento: "",
              })
            }
          >
            + Adicionar CIOT
          </Button>
        </div>

        {ciotsFieldArray.fields.length === 0 ? (
          <div className="text-muted small py-2">
            Nenhum CIOT vinculado.
          </div>
        ) : (
          ciotsFieldArray.fields.map((field, index) => {
            const docResponsavel = form.watch(`ciots.${index}.documento`);
            const temDocResponsavel = Boolean(
              docResponsavel && docResponsavel.trim().length > 0
            );
            const ciotError = form.formState.errors.ciots?.[index];
            const docError =
              ciotError?.documento ||
              (ciotError && !ciotError.ciot ? (ciotError as any) : undefined);

            return (
              <div key={field.id} className="mb-3 p-3 bg-light rounded border">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold text-secondary small">
                    CIOT #{index + 1}
                  </span>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => ciotsFieldArray.remove(index)}
                  >
                    Remover
                  </Button>
                </div>

                <Row className="g-3 align-items-start">
                  <Col xs={12} md={6}>
                    <DropdownFilter<PessoaDto>
                      fetchData={pessoaBuscar.PessoaBuscar}
                      loading={pessoaBuscar.loading}
                      labelDescription="Pesquisar Responsável (CPF / CNPJ) *"
                      getDescription={(item: PessoaDto) =>
                        `${item.nome} (${formatarDoc(item.documento)})`
                      }
                      getId={(item: PessoaDto) => item.id}
                      registeredFields={{
                        id: form.register(`ciots.${index}.nomeResponsavel`),
                        description: form.register(
                          `ciots.${index}.nomeResponsavel`
                        ),
                      }}
                      errorsMessageDescription={
                        !temDocResponsavel && docError
                          ? {
                              type: "required",
                              message:
                                docError.message ||
                                "Selecione o responsável pelo CIOT",
                            }
                          : undefined
                      }
                      onSelect={(item: PessoaDto | undefined) => {
                        if (item) {
                          form.setValue(
                            `ciots.${index}.nomeResponsavel`,
                            item.nome
                          );
                          form.setValue(
                            `ciots.${index}.documento`,
                            formatarDoc(item.documento),
                            { shouldValidate: true }
                          );
                        }
                      }}
                    />
                    <div className="mt-1">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none text-muted"
                        onClick={() => setCadastroCiotIndex(index)}
                      >
                        + Cadastrar
                      </Button>
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <FloatInput
                      label="CPF / CNPJ do Responsável *"
                      inputMask="cnpj-cpf"
                      maxLength={18}
                      registerField={form.register(`ciots.${index}.documento`, {
                        onChange: (e) => {
                          const limpo = (e.target.value || "").replace(
                            /[^A-Za-z0-9]/g,
                            ""
                          );
                          if (!limpo) {
                            form.setValue(`ciots.${index}.nomeResponsavel`, "");
                          }
                        },
                      })}
                      errorsMessage={docError}
                    />
                  </Col>
                </Row>

                {/* Abaixo, após a seleção do cnpj/cpf do ciot deve-se exibir o campo ciot */}
                {temDocResponsavel ? (
                  <Row className="g-3 mt-1">
                    <Col xs={12} md={6}>
                      <FloatInput
                        label="Código CIOT (12 dígitos) *"
                        inputMask="int"
                        maxLength={12}
                        registerField={form.register(`ciots.${index}.ciot`)}
                        errorsMessage={ciotError?.ciot}
                      />
                      <div className="mt-1">
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="p-0 text-decoration-none text-primary small d-inline-flex align-items-center"
                          onClick={() => handleGerarCiot(index)}
                        >
                          <FontAwesomeIcon
                            icon={faBolt}
                            className="me-1 text-warning"
                          />
                          <span>Gerar CIOT automaticamente (ANTT)</span>
                        </Button>
                      </div>
                    </Col>
                  </Row>
                ) : (
                  <div className="text-muted small mt-2">
                    Selecione ou informe o CPF/CNPJ do responsável acima para preencher o código CIOT.
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* 3. Vales-Pedágio */}
      <div className="card shadow-sm p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="text-primary mb-0">Vales-Pedágio (valePed)</h5>
            <small className="text-muted">
              Dados da fornecedora e do responsável pelo pagamento do Vale-Pedágio obrigatório
            </small>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              valesPedagioFieldArray.append({
                nomeFornecedoraValePedagio: "",
                cnpjEmitenteValePedagio: "",
                nomeResponsavelPagamento: "",
                documentoResponsavelPagamento: "",
                numeroComprovante: "",
                valorValePedagio: undefined as any,
                tipoValePedagio: ETipoValePedagioMDFe.TagEletronica,
                categoriaCombinacaoVeicular: "",
              })
            }
          >
            + Adicionar Vale-Pedágio
          </Button>
        </div>

        {valesPedagioFieldArray.fields.length === 0 ? (
          <div className="text-muted small py-2">
            Nenhum Vale-Pedágio vinculado.
          </div>
        ) : (
          valesPedagioFieldArray.fields.map((field, index) => (
            <div key={field.id} className="mb-3 p-3 bg-light rounded border">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold text-secondary small">
                  Vale-Pedágio #{index + 1}
                </span>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => valesPedagioFieldArray.remove(index)}
                >
                  Remover
                </Button>
              </div>

              {/* 1. Empresa Fornecedora do Vale-Pedágio */}
              <Row className="g-3 mb-2 align-items-start">
                <Col xs={12} md={6}>
                  <DropdownFilter<PessoaDto>
                    fetchData={pessoaBuscar.PessoaBuscar}
                    loading={pessoaBuscar.loading}
                    labelDescription="Pesquisar Empresa Fornecedora"
                    getDescription={(item: PessoaDto) =>
                      `${item.nome} (${item.documento})`
                    }
                    getId={(item: PessoaDto) => item.id}
                    registeredFields={{
                      id: form.register(
                        `valesPedagio.${index}.nomeFornecedoraValePedagio`
                      ),
                      description: form.register(
                        `valesPedagio.${index}.nomeFornecedoraValePedagio`
                      ),
                    }}
                    errorsMessageDescription={
                      form.formState.errors.valesPedagio?.[index]
                        ?.nomeFornecedoraValePedagio
                    }
                    onSelect={(item: PessoaDto | undefined) => {
                      if (item) {
                        form.setValue(
                          `valesPedagio.${index}.nomeFornecedoraValePedagio`,
                          item.nome
                        );
                        form.setValue(
                          `valesPedagio.${index}.cnpjEmitenteValePedagio`,
                          formatarDoc(item.documento),
                          { shouldValidate: true }
                        );
                      }
                    }}
                  />
                  <div className="mt-1">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none text-muted"
                      onClick={() => setCadastroValePedagioIndex(index)}
                    >
                      + Cadastrar
                    </Button>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <FloatInput
                    label="CNPJ da Empresa Fornecedora *"
                    inputMask="cnpj"
                    maxLength={18}
                    registerField={form.register(
                      `valesPedagio.${index}.cnpjEmitenteValePedagio`
                    )}
                    errorsMessage={
                      form.formState.errors.valesPedagio?.[index]
                        ?.cnpjEmitenteValePedagio
                    }
                  />
                </Col>
              </Row>

              {/* 2. Responsável pelo Pagamento do Vale-Pedágio */}
              <Row className="g-3 mb-2 align-items-start">
                <Col xs={12} md={6}>
                  <DropdownFilter<PessoaDto>
                    fetchData={pessoaBuscar.PessoaBuscar}
                    loading={pessoaBuscar.loading}
                    labelDescription="Pesquisar Responsável pelo Pagamento"
                    getDescription={(item: PessoaDto) =>
                      `${item.nome} (${item.documento})`
                    }
                    getId={(item: PessoaDto) => item.id}
                    registeredFields={{
                      id: form.register(
                        `valesPedagio.${index}.nomeResponsavelPagamento`
                      ),
                      description: form.register(
                        `valesPedagio.${index}.nomeResponsavelPagamento`
                      ),
                    }}
                    errorsMessageDescription={
                      form.formState.errors.valesPedagio?.[index]
                        ?.nomeResponsavelPagamento
                    }
                    onSelect={(item: PessoaDto | undefined) => {
                      if (item) {
                        form.setValue(
                          `valesPedagio.${index}.nomeResponsavelPagamento`,
                          item.nome
                        );
                        form.setValue(
                          `valesPedagio.${index}.documentoResponsavelPagamento`,
                          formatarDoc(item.documento),
                          { shouldValidate: true }
                        );
                      }
                    }}
                  />
                  <div className="mt-1">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none text-muted"
                      onClick={() =>
                        setCadastroResponsavelValePedagioIndex(index)
                      }
                    >
                      + Cadastrar
                    </Button>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <FloatInput
                    label="CPF / CNPJ do Responsável pelo Pagamento"
                    inputMask="cnpj-cpf"
                    maxLength={18}
                    registerField={form.register(
                      `valesPedagio.${index}.documentoResponsavelPagamento`
                    )}
                    errorsMessage={
                      form.formState.errors.valesPedagio?.[index]
                        ?.documentoResponsavelPagamento ||
                      form.formState.errors.valesPedagio?.[index]
                        ?.cnpjResponsavelPagamento ||
                      form.formState.errors.valesPedagio?.[index]
                        ?.cpfResponsavelPagamento
                    }
                  />
                </Col>
              </Row>

              <Row className="g-3">
                <Col xs={12} md={3}>
                  <FloatInput
                    label="Número do Comprovante de Compra *"
                    maxLength={20}
                    registerField={form.register(
                      `valesPedagio.${index}.numeroComprovante`
                    )}
                    errorsMessage={
                      form.formState.errors.valesPedagio?.[index]
                        ?.numeroComprovante
                    }
                  />
                </Col>
                <Col xs={12} md={3}>
                  <FloatNumericInput
                    label="Valor do Vale-Pedágio (R$) *"
                    name={`valesPedagio.${index}.valorValePedagio`}
                    control={form.control}
                    decimalScale={2}
                    errorsMessage={
                      form.formState.errors.valesPedagio?.[index]
                        ?.valorValePedagio
                    }
                  />
                </Col>
                <Col xs={12} md={3}>
                  <FloatSelect
                    label="Tipo do Vale-Pedágio"
                    options={tipoValePedagioMDFeOptions}
                    register={form.register(
                      `valesPedagio.${index}.tipoValePedagio`
                    )}
                  />
                </Col>
                <Col xs={12} md={3}>
                  <FloatSelect
                    label="Categ. Combinação Veicular *"
                    options={categoriaCombinacaoVeicularOptions}
                    register={form.register(
                      `valesPedagio.${index}.categoriaCombinacaoVeicular`
                    )}
                    errorsMessage={
                      form.formState.errors.valesPedagio?.[index]
                        ?.categoriaCombinacaoVeicular
                    }
                  />
                </Col>
              </Row>
            </div>
          ))
        )}
      </div>

      <WizardFooter onVoltar={handleVoltar} />
    </Form>

    <MDFeModalGerarCIOTView
      show={ciotModal !== null}
      inicial={ciotModal?.inicial ?? null}
      onClose={() => setCiotModal(null)}
      onGerado={handleCiotGerado}
    />
    </>
  );
}

export default MDFeCadastroContratantesView;
